import { LoadingButton } from '@mui/lab'
import { Button, Checkbox, Link, Popover, Stack, Typography, useTheme } from '@mui/material'
import { sourcesGetGmailPreview, sourcesNewGmailQuery } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, GmailEmailSummary, GmailQuery, OnboardingStep, SearchScopeType } from 'api/model'
import { COLORS } from 'app-constants'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import JelledModal from 'components/JelledModal'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { format } from 'date-fns'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useActiveOrganization } from 'hooks/use-organization'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { myTwinAtom } from 'state/my-twin'
import { useFetchTwinResponderLabels } from 'hooks/use-email-responder'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { getLabelDisplayText } from 'components/Gmail/SettingsModal'

const AddEmailQueryModal = ({
  sourceId,
  onClose,
  onEmailIndexAdded,
  gmailQueries,
  updateOnboardingStep,
}: {
  sourceId: string
  onClose: () => void
  onEmailIndexAdded: (gmailQuery: GmailQuery) => void
  gmailQueries: GmailQuery[]
  updateOnboardingStep: () => void
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const [user] = useRecoilState(authenticatedUserAtom)
  const hasSetTwinProfile = !!user?.onboarding_steps_completed.includes(OnboardingStep.set_twin_profile)
  const myTwin = useRecoilValue(myTwinAtom)
  useFetchCheckPermissions(Feature.email_responder)
  const { twinResponderLabels } = useFetchTwinResponderLabels(myTwin?.id ?? '')

  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false)
  const [querySubmitCount, setQuerySubmitCount] = useState(0)

  const [isSubmittingIndex, setIsSubmittingIndex] = useState(false)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [emailSummaries, setEmailSummaries] = useState<GmailEmailSummary[]>([])

  const [keepEmailContentPrivate, setKeepEmailContentPrivate] = useState<boolean>(false)

  // if we remove the search query, clear the email summaries as well
  useEffect(() => {
    if (searchText) {
      setEmailSummaries([])
      setQuerySubmitCount(0)
    }
  }, [searchText])

  const onSearchEmails = async () => {
    setIsSubmittingQuery(true)
    setQuerySubmitCount((submitCount) => submitCount + 1)

    if (!activeOrganization || !searchText) {
      return
    }

    try {
      const response = await sourcesGetGmailPreview(activeOrganization, sourceId, {
        query: searchText,
        search_scope: keepEmailContentPrivate ? SearchScopeType.owner : SearchScopeType.global,
      })
      setEmailSummaries(response.data)
    } catch (Error) {
      setToast({ title: 'Failed to search emails', severity: 'error' })
    } finally {
      setIsSubmittingQuery(false)
    }
  }

  const onAddEmailIndex = async () => {
    setIsSubmittingIndex(true)

    if (!activeOrganization || !searchText) {
      return null
    }

    try {
      const response = await sourcesNewGmailQuery(activeOrganization, sourceId, {
        query: searchText,
        search_scope: keepEmailContentPrivate ? SearchScopeType.owner : SearchScopeType.global,
      })
      onEmailIndexAdded(response.data)
      setToast({ title: 'Email index added', severity: 'success' })

      // check to see if user onboarding step includes OnboardingStep.set_twin_profile, if not, then we should update onboarding step
      if (!hasSetTwinProfile) {
        updateOnboardingStep()
      }
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to add email index', severity: 'error' })
    } finally {
      setIsSubmittingIndex(false)
    }
  }

  const onFormSubmit = async () => {
    setIsSubmittingIndex(true)

    const existingQuery = gmailQueries.find((q) => q.query === searchText)

    if (!activeOrganization || !searchText || emailSummaries.length === 0 || existingQuery) {
      setIsSubmittingIndex(false)
      return
    }

    await onAddEmailIndex()
  }

  // check whether or not we've indexed this query already
  const isExistingQuery = !!gmailQueries.find((q) => {
    return searchText.toLowerCase().includes(q.query.toLowerCase())
  })

  const renderErrorText = () => {
    if (!searchText && querySubmitCount > 0) {
      return 'You must enter a search query'
    } else if (isExistingQuery) {
      return 'This query has already been added'
    }
    return ''
  }

  // labels popover
  const [labelsAnchorEl, setLabelsAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLabelsAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setLabelsAnchorEl(null)
  }
  const open = Boolean(labelsAnchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <JelledModal
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Stack>
        <Typography mb={2} variant="h4" color="secondary">
          New query
        </Typography>

        <Typography width="100%" variant="pS" mb={5} color="secondary">
          Use{' '}
          <Link
            href="https://support.google.com/mail/answer/7190?hl=en"
            rel="no-referrer"
            target="blank"
            sx={{
              width: 'fit-content',
              display: 'inline',
            }}>
            <Typography
              variant="body0"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              search operators
            </Typography>
          </Link>{' '}
          to select emails to be indexed into your twin’s knowledge base.
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <TextFieldWithDarkMode
            className="has-label"
            theme={theme}
            isDarkBackdrop={false}
            label="Query"
            placeholder="from:me in:work"
            value={searchText}
            onChange={onSearchTextChange}
            disabled={isSubmittingQuery}
            InputProps={{
              endAdornment: (
                <Stack>
                  <Stack
                    component="button"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    p="0px 0 0px 8px"
                    borderRadius="6px"
                    border="1px solid #EEE"
                    onClick={handleClick}
                    sx={{
                      background: COLORS.GRAY_8,
                      cursor: 'pointer',
                    }}>
                    <Typography
                      variant="pS"
                      sx={{
                        color: COLORS.GRAY_3,
                      }}>
                      Labels{' '}
                    </Typography>

                    <Stack
                      sx={{
                        ml: 1,
                        mr: -1,
                      }}>
                      <KeyboardArrowDownIcon
                        sx={{
                          color: COLORS.GRAY_3,
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Popover
                    id={id}
                    open={open}
                    anchorEl={labelsAnchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}>
                    <Stack
                      p="16px 8px"
                      spacing="4px"
                      sx={{
                        maxHeight: '360px',
                        overflowY: 'scroll',
                        width: '304px',
                      }}>
                      {twinResponderLabels.map((label, index) => (
                        <Stack
                          key={index}
                          borderRadius="8px"
                          p={1}
                          alignItems="center"
                          gap={1}
                          direction="row"
                          onClick={() => {
                            // if the label is already in the search text, remove it
                            if (searchText.includes(`label:${getLabelDisplayText(label.name)}`)) {
                              setSearchText(searchText.replace(` label:${getLabelDisplayText(label.name)}`, ''))
                            } else if (!searchText.includes(`label:`)) {
                              // if there is no label in the search text, add it
                              setSearchText(`${searchText} label:${getLabelDisplayText(label.name)}`)
                            } else {
                              // otherwise, replace the existing label with the newly selected label
                              const newSearchText = searchText.replace(
                                /label:[^ ]+/,
                                `label:${getLabelDisplayText(label.name)}`
                              )
                              setSearchText(newSearchText)
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            background: searchText.includes(`label:${getLabelDisplayText(label.name)}`)
                              ? COLORS.JELLED_MID_BLUE_10_PERCENT
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: searchText.includes(`label:${getLabelDisplayText(label.name)}`)
                                ? COLORS.JELLED_MID_BLUE_10_PERCENT
                                : COLORS.GRAY_8,
                            },
                          }}>
                          {searchText.includes(`label:${getLabelDisplayText(label.name)}`) ? (
                            <CheckCircleIcon
                              sx={{
                                color: COLORS.JELLED_PRIMARY_BLUE,
                              }}
                            />
                          ) : (
                            <AddCircleOutlineIcon
                              sx={{
                                color: COLORS.GRAY_2,
                              }}
                            />
                          )}
                          <Typography
                            variant="h6"
                            sx={{
                              color: COLORS.GRAY_2,
                            }}>
                            {getLabelDisplayText(label.name)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Popover>
                </Stack>
              ),
            }}
            inputProps={{
              sx: {
                width: '100%',
                '& .MuiSvgIcon-root': {
                  marginRight: '0',
                },
              },
            }}
            sx={{
              width: '520px',
            }}
          />
          <LoadingButton
            disabled={!searchText || isExistingQuery}
            loading={isSubmittingQuery}
            onClick={() => onSearchEmails()}
            variant="contained"
            color="primary">
            Preview
          </LoadingButton>
        </Stack>

        <Typography variant="pXS" color="error" my={1}>
          {renderErrorText()}
        </Typography>

        {emailSummaries.length > 0 && (
          <Typography variant="pXS" color="#999999" my={1}>
            Only the top five results are shown
          </Typography>
        )}
        {!isSubmittingQuery && querySubmitCount > 0 && emailSummaries.length === 0 && (
          <Typography variant="h6" color="#999999" my={1}>
            No results found, please try a different query
          </Typography>
        )}

        {emailSummaries.length > 0 && (
          <Stack
            sx={{
              p: 2,
              mb: 3,
              overflowY: 'scroll',
              backgroundColor: COLORS.GRAY_8,
              borderRadius: '8px',
              overflow: 'hidden',
            }}>
            {emailSummaries.slice(0, 10).map((n, index) => {
              return (
                <Stack key={index} pr={2} py={1}>
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    justifyContent="space-between">
                    <Typography variant="h6" color="secondary">
                      {n.subject}
                    </Typography>
                    <Typography width="200px" variant="pXS" textAlign={isMobile ? 'left' : 'right'} color="secondary">
                      {format(new Date(n.date), 'MMM dd, yyyy')} at {format(new Date(n.date), 'hh:mm a')}
                    </Typography>
                  </Stack>
                  <Typography variant="pS" color="#666666">
                    {n.from}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        )}

        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <Checkbox
            checked={keepEmailContentPrivate}
            color="primary"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setKeepEmailContentPrivate(checked)
            }}
            sx={{
              marginRight: '0',
              padding: 0,
            }}
          />
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Keep email content private when interacting with others
          </Typography>
        </Stack>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          spacing={isMobile ? 2 : 0}>
          <Stack width="100%" direction="row" alignItems="center" spacing={2} justifyContent={'flex-end'}>
            <Button size="medium" onClick={() => onClose()} color="primary">
              Cancel
            </Button>
            <LoadingButton
              disabled={emailSummaries.length === 0 || isExistingQuery || !searchText}
              loading={isSubmittingIndex}
              variant="contained"
              size="medium"
              color="primary"
              onClick={onFormSubmit}>
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </JelledModal>
  )
}

export default withOnboardingStep(AddEmailQueryModal, OnboardingStep.set_twin_profile)
